import React, {ChangeEvent, CSSProperties, forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState} from 'react'
import moment from 'moment'

import Button from '../buttons/Button.js'
import {Colors} from '../index.js'
import IconButton from '../buttons/IconButton.js'
import S1 from '../typography/S1.js'
import Popover from '../overlays/Popover.js'
import Input from './Input.js'
import {FieldProps, FieldRef, InvalidProps} from './types.js'
import {field, fieldMargins} from './styles.js'

const monthLabels = ['januari', 'februari', 'maart', 'april', 'mei', 'juni', 'juli', 'augustus', 'september', 'oktober', 'november', 'december']
const dayLabels = ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo']

// Dates are YYYY-MM-DD strings
export interface DatePickerRange {
    startDate: string
    endDate: string
    clickedDate?: string
}

// Without range: an input change event or a mouse event whose target.value was set to the picked date
export interface DatePickerChangeEvent {
    target: {value: string}
}

export interface DatePickerProps extends FieldProps, InvalidProps {
    value: string
    // Called with a DatePickerChangeEvent without range, or with a new range object with range;
    // noBubble is true when only the start date of a range was clicked
    onChange?: (eventOrRange: DatePickerChangeEvent | DatePickerRange, noBubble?: boolean) => void
    range?: DatePickerRange
    minDate?: string
    maxDate?: string
    maxRange?: number // in days
    allowEmpty?: boolean
}

interface DatePickerState {
    inputFocused: boolean
    value: string
    displayDate: string
    dateHover: {
        [date: string]: boolean
    }
    range: DatePickerRange
    mode: '' | 'selectRange'
    // Message shown on the input when a typed date is outside minDate/maxDate/maxRange, '' when valid
    invalid: string
}

export interface DatePickerRef extends FieldRef {
    close: () => void
}

// The text of a range in the field, empty without a start date
function rangeText(range: {startDate: string, endDate: string}) {
    if (!range.startDate) {
        return ''
    }

    return range.startDate !== range.endDate ? `${moment(range.startDate).format('DD-MM-YY')} t/m ${moment(range.endDate).format('DD-MM-YY')}` : moment(range.startDate).format('DD-MM-YY')
}

const DatePicker = forwardRef<DatePickerRef, DatePickerProps>((props, ref) => {
    const {label, info, value, readOnly, disabled, isInvalid, style = {}, minDate, maxDate, maxRange, allowEmpty} = props

    // Neither disabled nor readOnly ever reports a change
    const onChange: DatePickerProps['onChange'] = (eventOrRange, noBubble) => {
        if (!disabled && !readOnly) {
            props.onChange?.(eventOrRange, noBubble)
        }
    }

    const popover = useRef<{show: () => void, close: () => void, closed: () => boolean}>(null)
    const formControl = useRef<{focus: () => void, blur: () => void}>(null)
    // The field's outer element, the calendar opens at it
    const anchorRef = useRef<HTMLDivElement>(null)

    // Set by setInvalid, shown until the next change
    const [isInvalidState, setIsInvalidState] = useState(false)

    const [state, setFullState] = useState<DatePickerState>(() => {
        return {
            inputFocused: false,
            value: props.range ? rangeText(props.range) : (props.value ? moment(props.value).format('DD-MM-YY') : ''),
            displayDate: props.value,
            dateHover: {},
            range: props.range ? {...props.range, clickedDate: ''} : undefined,
            mode: '',
            invalid: ''
        }
    })
    // Merges like class setState
    const setState = (partial: Partial<DatePickerState>) => setFullState((prev) => ({...prev, ...partial}))

    // Read by the blur timeout, which runs after the render that created it
    const latest = useRef({props, state})
    latest.current = {props, state}

    const blurTimeout = useRef<ReturnType<typeof setTimeout>>(null)

    // A single typed date in range mode is held back until the user is done typing, so the caller doesn't
    // get a one-day range while the user is still on their way to typing the end date
    const pendingRange = useRef<DatePickerRange | null>(null)

    const commitPendingRange = () => {
        if (pendingRange.current) {
            const newRange = pendingRange.current
            pendingRange.current = null
            const {disabled, readOnly, onChange} = latest.current.props
            if (!disabled && !readOnly) {
                onChange?.(newRange)
            }
        }
    }

    const prevValues = useRef({value, startDate: props.range?.startDate, endDate: props.range?.endDate})
    useLayoutEffect(() => {
        const {range} = props
        const prev = prevValues.current
        if (prev.value !== value || prev.startDate !== range?.startDate || prev.endDate !== range?.endDate) {
            if (range) {
                const newRange = {
                    startDate: range.startDate,
                    endDate: range.endDate,
                    clickedDate: range.startDate
                }
                setState({value: rangeText(range), range: newRange, displayDate: range.startDate, invalid: ''})
            } else {
                setState({value: value ? moment(value).format('DD-MM-YY') : '', displayDate: value, invalid: ''})
            }
        }
        prevValues.current = {value, startDate: range?.startDate, endDate: range?.endDate}
    }, [value, props.range?.startDate, props.range?.endDate])

    useEffect(() => {
        return () => {
            clearTimeout(blurTimeout.current)
        }
    }, [])

    useImperativeHandle(ref, () => ({
        focus: () => formControl.current?.focus(),
        blur: () => formControl.current?.blur(),
        setInvalid: (isInvalid = true) => {
            setIsInvalidState(isInvalid)
            formControl.current?.focus()
        },
        close: () => {
            setTimeout(() => {
                popover.current?.close()
                formControl.current?.blur()
            }, 50)
        }
    }), [])

    // Same rules as the calendar uses to disable days
    const isOutOfBounds = (date: string) => {
        return (minDate && date < minDate) || (maxDate && date > maxDate)
    }

    const exceedsMaxRange = (startDate: string, endDate: string) => {
        return maxRange && moment(endDate).isAfter(moment(startDate).add(maxRange, 'days').format('YYYY-MM-DD'))
    }

    const getInvalidMessage = (startDate: string, endDate: string) => {
        if (isOutOfBounds(startDate) || isOutOfBounds(endDate)) {
            return 'Deze datum is niet toegestaan'
        }
        if (exceedsMaxRange(startDate, endDate)) {
            return `De periode mag maximaal ${maxRange} dagen zijn`
        }
        return ''
    }

    const onFocus = () => {
        if (popover.current.closed()) {
            setState({mode: ''})
        }

        popover.current.show()
        setState({
            inputFocused: true
        })
    }

    const onBlur = () => {
        const {range} = props

        setState({inputFocused: false})

        clearTimeout(blurTimeout.current)
        blurTimeout.current = setTimeout(() => {
            const {value} = latest.current.props

            if (!latest.current.state.inputFocused && !range) {
                popover.current?.close()
                setState({value: value ? moment(value).format('DD-MM-YY') : '', invalid: ''})
            }

            // Calendar clicks refocus the input, so this only commits when the user really left the field
            if (!latest.current.state.inputFocused && range) {
                commitPendingRange()
            }
        }, 100)
    }

    const onChangeInput = (event: ChangeEvent<HTMLInputElement>) => {
        const {range} = props

        if (disabled || readOnly) {
            return
        }

        setIsInvalidState(false)

        let value = event.target.value

        if (state.value.length === 1 && value.length === 2) {
            value = `${value}-`
        }

        if (state.value.length === 4 && value.length === 5) {
            value = `${value}-`
        }

        // scheiding
        if (range && state.value.length === 8 && value.length === 9) {
            value = `${value}t/m `
        }

        if (range && state.value.length === 13 && value.length === 12) {
            value = value.replace(' t/m', '')
        }

        if (range && state.value.length === 14 && value.length === 13) {
            value = value.replace(' t/m ', '')
        }

        if (range && state.value.length === 14 && value.length === 15) {
            value = `${value}-`
        }

        if (range && state.value.length === 17 && value.length === 18) {
            value = `${value}-`
        }

        if (!range) {
            if (!/^[0-9]{1,2}[-]{2}[0-9]{1,2}$/.test(value)) {
                value = value.replace('--', '-')
            }

            if (/^$|^[0-9]{0,2}[-]?[0-9]{0,2}[-]?[0-9]{0,2}$/.test(value)) {
                setState({value, invalid: ''})

                if (/^[0-9]{2}-[0-9]{2}-[0-9]{2}$/.test(value) && moment(value, 'DD-MM-YY').isValid()) {
                    const date = moment(value, 'DD-MM-YY').format('YYYY-MM-DD')
                    // Show the typed month in the calendar
                    setState({displayDate: date})
                    const invalid = getInvalidMessage(date, date)
                    if (invalid) {
                        setState({invalid})
                    } else {
                        event.target.value = date
                        onChange(event)
                    }
                } else if (allowEmpty && value === '') {
                    onChange(event)
                }
            }
        }

        if (range) {
            pendingRange.current = null

            // regex voor tijdens het typen...
            // en dubbele streepjes
            if (/^$|^[0-9]{0,2}[-]?[0-9]{0,2}[-]?[0-9]{0,2}?[\s]?[^.]{0,3}?[\s]?[0-9]{0,2}[-]?[0-9]{0,2}[-]?[0-9]{0,2}$/.test(value)) {
                value = value.replace('--', '-')
                setState({value, invalid: ''})
            }

            const dates = value.split(' t/m ')
            // regex voor de dubbele datum
            const isCompleteRange = dates.length === 2 && /^$|^[0-9]{2}[-][0-9]{2}[-][0-9]{2}?[\s]?[^.]{3}?[\s]?[0-9]{2}[-][0-9]{2}[-]?[0-9]{2}$/.test(value)

            if (isCompleteRange) {
                if (moment(dates[0], 'DD-MM-YY').isValid() && moment(dates[1], 'DD-MM-YY').isValid()) {
                    let startDate = moment(dates[0], 'DD-MM-YY').format('YYYY-MM-DD')
                    let endDate = moment(dates[1], 'DD-MM-YY').format('YYYY-MM-DD')
                    // Show the month of the date that was typed last
                    setState({displayDate: endDate})
                    if (endDate < startDate) {
                        [startDate, endDate] = [endDate, startDate]
                    }

                    const invalid = getInvalidMessage(startDate, endDate)
                    if (invalid) {
                        setState({invalid})
                    } else {
                        const newRange = {startDate, endDate, clickedDate: startDate}
                        setState({range: newRange})
                        onChange(newRange)
                    }
                }
            } else if (/^[0-9]{2}-[0-9]{2}-[0-9]{2}$/.test(dates[0]) && moment(dates[0], 'DD-MM-YY').isValid()) {
                // A complete start date, with the end date not (fully) typed yet
                const date = moment(dates[0], 'DD-MM-YY').format('YYYY-MM-DD')
                setState({displayDate: date})
                const invalid = getInvalidMessage(date, date)
                if (invalid) {
                    setState({invalid})
                } else {
                    const newRange = {...state.range, startDate: date, endDate: date}
                    setState({range: newRange})
                    pendingRange.current = newRange
                }
            }
        }
    }

    const onClickPrevious = () => {
        const {displayDate} = state

        const newDate = moment(displayDate).subtract(1, 'months').format('YYYY-MM-DD')

        setState({displayDate: newDate})

        setTimeout(() => {
            formControl.current?.focus()
        }, 1)
    }

    const onClickNext = () => {
        const {displayDate} = state

        const newDate = moment(displayDate).add(1, 'months').format('YYYY-MM-DD')

        setState({displayDate: newDate})

        setTimeout(() => {
            formControl.current?.focus()
        }, 1)
    }

    const onClickRange = (date: string) => {
        const {range, mode} = state

        pendingRange.current = null
        setIsInvalidState(false)

        if (range) {
            if (mode !== 'selectRange') {
                const newRange = {startDate: date, endDate: date, clickedDate: date}

                onChange(newRange, true)

                const value = `${moment(newRange.startDate).format('DD-MM-YY')}`
                setState({range: newRange, mode: 'selectRange', value: value, invalid: ''})
            } else {
                const newRange = {...range}
                // Enforce maxRange
                if (maxRange && newRange.startDate) {
                    const maxEndDate = moment(newRange.startDate).add(maxRange, 'days').format('YYYY-MM-DD')
                    if (moment(date).isAfter(maxEndDate)) {
                        newRange.endDate = maxEndDate
                    }
                }
                newRange.clickedDate = ''

                onChange(newRange)

                const value = newRange.startDate !== newRange.endDate ? `${moment(newRange.startDate).format('DD-MM-YY')} t/m ${moment(newRange.endDate).format('DD-MM-YY')}` : moment(newRange.startDate).format('DD-MM-YY')
                setState({range: newRange, mode: '', value: value, invalid: ''})
            }

            setTimeout(() => {
                formControl.current?.focus()
            }, 1)
        }
    }

    const onHoverRange = (date: string) => {
        const {range, mode} = state

        if (mode === 'selectRange') {
            if (date < range.clickedDate) {
                setState({range: {...range, startDate: date, endDate: range.clickedDate}})
            } else {
                setState({range: {...range, startDate: range.clickedDate, endDate: date}})
            }
        }
    }

    const onMouseLeaveRange = () => {
        const {range, mode} = state

        if (mode === 'selectRange') {
            setState({range: {...range, startDate: range.clickedDate, endDate: range.clickedDate}})
        }
    }

    const onClickDate = (date: string, event: React.MouseEvent<HTMLTableCellElement>) => {
        // The cell's target gets the date as value, so callers can read event.target.value like an input change event
        const changeEvent = event as unknown as DatePickerChangeEvent
        changeEvent.target.value = date

        setIsInvalidState(false)
        formControl.current?.blur()
        onChange(changeEvent)
    }

    const onClickToday = (event: React.MouseEvent<HTMLButtonElement>) => {
        const {range} = props

        const today = moment().format('YYYY-MM-DD')

        setIsInvalidState(false)

        if (range) {
            const newRange = {startDate: today, endDate: today, clickedDate: today}

            pendingRange.current = null
            onChange(newRange)

            const value = `${moment(newRange.startDate).format('DD-MM-YY')}`
            setState({range: newRange, mode: 'selectRange', value: value, displayDate: today, invalid: ''})

            setTimeout(() => {
                formControl.current?.focus()
            }, 1)
        } else {
            const changeEvent = event as unknown as DatePickerChangeEvent
            changeEvent.target.value = today
            formControl.current?.blur()
            onChange(changeEvent)
        }
    }

    let {displayDate} = state
    const {mode, range} = state

    // Necessary to prevent moment from throwing an error when value is empty or invalid
    if (!displayDate) {
        displayDate = undefined
    }

    const today = moment().format('YYYY-MM-DD')
    const year = moment(displayDate).year()
    const month = moment(displayDate).month()
    let date = moment(displayDate).startOf('month').startOf('isoWeek').format('YYYY-MM-DD')
    const lastDay = moment(displayDate).endOf('month').endOf('isoWeek').format('YYYY-MM-DD')

    const weeks: string[][] = [[]]

    while (date <= lastDay) {
        let week = weeks[weeks.length - 1]

        if (week.length === 7) {
            weeks.push([])
            week = weeks[weeks.length - 1]
        }

        week.push(date)
        date = moment(date).add(1, 'day').format('YYYY-MM-DD')
    }

    // This wrapper is the field's outer element: it takes the field margins and the caller's style (with flex 1 even
    // with a width, like before). The Input inside has no margins of its own, so the wrapper has the box's edges and
    // the calendar opens at it. Clicking it opens the calendar, also when the input is focused already.
    return (
        <div
            style={{flex: 1, height: field.height, ...fieldMargins, ...style}}
            ref={anchorRef}
            onClick={() => popover.current?.show()}
        >
            {/* Without children it renders nothing here, the calendar is in a portal on the body */}
            <Popover
                anchorRef={anchorRef}
                popoverStyle={{overflow: 'hidden', width: 280, minWidth: 280}}
                disabled={disabled || readOnly}
                noClose={true}
                ref={(ref) => {popover.current = ref}}
                onOutsideClick={() => {
                    if (range && mode === 'selectRange') {
                        onChange(range)

                        const value = range.startDate !== range.endDate ? `${moment(range.startDate).format('DD-MM-YY')} t/m ${moment(range.endDate).format('DD-MM-YY')}` : moment(range.startDate).format('DD-MM-YY')
                        setState({mode: '', value, invalid: ''})
                    }
                    commitPendingRange()
                }}
                content={
                    <div style={{width: 280, padding: 10}}>
                        <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-around'}}>
                            <IconButton onMouseDown={onClickPrevious}>
                                <i className='mdi mdi-chevron-left' />
                            </IconButton>
                            <div style={{flex: 1, textAlign: 'center'}}>
                                <S1 style={{textTransform: 'capitalize'}}>{monthLabels[month]} {year}</S1>
                            </div>

                            <IconButton onMouseDown={onClickNext}>
                                <i className='mdi mdi-chevron-right' />
                            </IconButton>
                        </div>

                        <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                            <table>
                                <thead>
                                    <tr>
                                        {dayLabels.map((label) => {
                                            return (
                                                <td
                                                    key={label}
                                                    style={{textAlign: 'center', width: 35, height: 35, fontSize: 12, color: Colors.textMedium}}
                                                >
                                                    {label}
                                                </td>
                                            )
                                        })}
                                    </tr>
                                </thead>
                                <tbody>
                                    {weeks.map((week, index) => {
                                        return (
                                            <tr key={`tr-${index}`}>
                                                {week.map((date) => {
                                                    let disabled = false
                                                    const currentMonth = moment(date).month() === moment(displayDate).month()

                                                    if (isOutOfBounds(date)) {
                                                        disabled = true
                                                    }

                                                    // While selecting the second date, disable dates more than maxRange away from the clicked date;
                                                    // startDate/endDate follow the hover, so they can't be used here
                                                    if (range && range.clickedDate && mode === 'selectRange' && (exceedsMaxRange(range.clickedDate, date) || exceedsMaxRange(date, range.clickedDate))) {
                                                        disabled = true
                                                    }

                                                    const dateStyle: CSSProperties = {
                                                        position: 'relative',
                                                        textAlign: 'center',
                                                        cursor: disabled ? 'default' : 'pointer',
                                                        borderRadius: '50%',
                                                        width: 35,
                                                        height: 35,
                                                        fontSize: date === today ? 16 : 14,
                                                        fontWeight: date === today ? 500 : 400,
                                                        background: date === value ? Colors.buttonSolid : 'transparent',
                                                        color: disabled ?
                                                            Colors.grey30 : // Use a grey color for disabled days
                                                            (date === value ?
                                                                'white' :
                                                                (currentMonth ? Colors.textDark : Colors.textMedium))
                                                    }

                                                    if (range) {
                                                        // selected startDate / enDate btn
                                                        if (mode !== 'selectRange' && (date === range.startDate || date === range.endDate)) {
                                                            dateStyle.color = Colors.white
                                                            dateStyle.background = Colors.buttonSolid
                                                        }

                                                        if (mode === 'selectRange' && date === range.clickedDate) {
                                                            dateStyle.color = Colors.white
                                                            dateStyle.background = Colors.buttonSolid
                                                        }

                                                        if (mode === 'selectRange' && date !== range.clickedDate && (date === range.endDate || date === range.startDate)) {
                                                            // dateStyle.color = Colors.white
                                                            dateStyle.background = Colors.backgroundSelected
                                                        }
                                                    }

                                                    // background between range
                                                    const rangeStyle: CSSProperties = {
                                                        position: 'absolute',
                                                        width: '0',
                                                        height: '100%',
                                                        left: '-50%',
                                                        top: 0,
                                                        zIndex: -1,
                                                        pointerEvents: 'none'
                                                    }

                                                    if (range && (date < range.endDate && date > range.startDate)) {
                                                        rangeStyle.width = '200%'
                                                        rangeStyle.borderTop = `1px solid ${Colors.grey20}`
                                                        rangeStyle.borderBottom = `1px solid ${Colors.grey20}`
                                                        if (mode !== 'selectRange') {
                                                            rangeStyle.background = Colors.backgroundSelected
                                                            rangeStyle.borderTop = 'none'
                                                            rangeStyle.borderBottom = 'none'
                                                        }
                                                    }

                                                    if (range && date === range.startDate && range.startDate !== range.endDate) {
                                                        rangeStyle.width = '100%'
                                                        rangeStyle.left = '50%'
                                                        rangeStyle.borderTop = `1px solid ${Colors.grey20}`
                                                        rangeStyle.borderBottom = `1px solid ${Colors.grey20}`
                                                        if (mode !== 'selectRange') {
                                                            rangeStyle.background = Colors.backgroundSelected
                                                            rangeStyle.borderTop = 'none'
                                                            rangeStyle.borderBottom = 'none'
                                                        }
                                                    }

                                                    if (range && date === range.endDate && range.startDate !== range.endDate) {
                                                        rangeStyle.width = '100%'
                                                        rangeStyle.left = '-50%'
                                                        rangeStyle.borderTop = `1px solid ${Colors.grey20}`
                                                        rangeStyle.borderBottom = `1px solid ${Colors.grey20}`
                                                        if (mode !== 'selectRange') {
                                                            rangeStyle.background = ''
                                                            rangeStyle.borderTop = 'none'
                                                            rangeStyle.borderBottom = 'none'
                                                        }
                                                    }

                                                    const hoverStyle: CSSProperties = {
                                                        position: 'absolute',
                                                        borderRadius: '50%',
                                                        width: 35,
                                                        height: 35,
                                                        top: 0,
                                                        left: 0,
                                                        background: Colors.grey20,
                                                        opacity: !disabled && state.dateHover[date] ? 1 : 0,
                                                        mixBlendMode: 'multiply'
                                                    }

                                                    return (
                                                        <td
                                                            key={`td-${date}`}
                                                            onMouseDown={disabled ? () => {} : range ? () => onClickRange(date) : (event) => onClickDate(date, event)}
                                                            onMouseEnter={() => {
                                                                const dateHover: DatePickerState['dateHover'] = {}
                                                                dateHover[date] = true
                                                                setState({dateHover})
                                                                if (range) {
                                                                    onHoverRange(date)
                                                                }
                                                            }}
                                                            onMouseLeave={() => {
                                                                setState({dateHover: {}})
                                                                if (range) {
                                                                    onMouseLeaveRange()
                                                                }
                                                            }}
                                                            style={dateStyle}
                                                        >
                                                            {moment(date).date()}
                                                            <div key={`range-${date}`} style={rangeStyle}></div>
                                                            <div key={`hover-${date}`} style={hoverStyle}></div>
                                                        </td>
                                                    )
                                                })}
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>

                            <Button
                                style={{width: 200, marginTop: 15, marginLeft: 0}}
                                onMouseDown={onClickToday}
                            >
                                Vandaag
                            </Button>
                        </div>
                    </div>
                }
            />

            <Input
                // The wrapper above has the margins
                style={{marginLeft: 0, marginRight: 0, marginBottom: 0}}
                label={label}
                info={info}
                disabled={disabled}
                readOnly={readOnly}
                // The message of a typed date outside the allowed dates goes before the caller's/setInvalid's state
                isInvalid={state.invalid || isInvalid || isInvalidState}
                value={value === '2999-12-31' ? '' : state.value}
                onFocus={disabled || readOnly ? () => {} : onFocus}
                onBlur={onBlur}
                onChange={onChangeInput}
                onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                        commitPendingRange()
                    }
                }}
                ref={(ref) => {formControl.current = ref}}
                append='mdi mdi-calendar'
            />
        </div>
    )
})

DatePicker.displayName = 'DatePicker'

export default DatePicker
